import { DiscountResponse } from '@models/discount';
import { CreateOrderRequest, OrderLine, PaymentMethod } from '@models/order';
import calculateEmployeeDiscount from '@utils/employeeDiscount';

/**
 * 주문 요청의 금액을 계산한다.
 * 상품금액 → 할인(쿠폰·포인트·임직원) → 결제금액 → 선물 → 선불카드 → PG 결제금액 순으로 계산한다.
 * @param originalRequest
 * @param availableDiscounts 매장의 할인 정보 (임직원 할인 계산에 사용)
 */
const orderWithTotalAmount = (
    originalRequest: CreateOrderRequest,
    availableDiscounts?: DiscountResponse,
): CreateOrderRequest => {
    const request = structuredClone(originalRequest);
    const orderLines = [...request.orderLines];

    // 상품별 금액을 계산
    const newOrderLines: OrderLine[] = orderLines.map((line) => {
        const {
            itemPrice, quantity, personalOptions, defaultOptions,
        } = line;
        const options = [...personalOptions, ...defaultOptions]
            .map((optionGroup) => optionGroup.options)
            .flat();

        // 실제 요청에 포함하기 위한 옵션
        const newOptions = options.map((option) => {
            const optionTotalAmount = Math.max(option.optionPrice * option.quantity, 0);
            return { ...structuredClone(option), totalAmount: optionTotalAmount };
        });
        // 계산하기 위한 옵션
        const newOptionsForTotal = options.map((option) => {
            const optionTotalAmount = option.optionPrice * option.quantity;
            return { ...option, totalAmount: optionTotalAmount };
        });
        const optionAmount = newOptionsForTotal
            .map((option) => option.totalAmount)
            .reduce((acc, cur) => acc + cur, 0);
        const totalAmount = Math.max((itemPrice + optionAmount) * quantity, 0);
        return { ...line, totalAmount, options: newOptions };
    });

    const totalAmount = newOrderLines
        // 품절 여부 처리
        .filter(((item) => item.status === 'ON_SALE'))
        .map((item) => item.totalAmount)
        .reduce((acc, cur) => acc + cur, 0);

    // 임직원 할인. 자동 적용하지 않고, 사용자가 직접 선택(useEmployeeDiscount)한 경우에만 적용한다.
    const employeeDiscount = (request.useEmployeeDiscount && availableDiscounts?.employeeDiscount)
        ? calculateEmployeeDiscount({
            orderLines: newOrderLines,
            discounts: request.discounts || [],
            employeeDiscount: availableDiscounts.employeeDiscount,
            totalAmount,
        })
        : 0;

    // 임직원 할인 내역 추가
    const discounts = request.discounts || [];
    if (employeeDiscount
        && !discounts.find((it) => it.type === 'EMPLOYEE')) {
        discounts.push({
            type: 'EMPLOYEE',
            discountScope: 'ORDER',
            discountMethod: 'FIXED',
            discountAmount: employeeDiscount,
        });
    }

    // 쿠폰·포인트·임직원 등 모든 할인 합산 (결제 금액 = totalAmount - totalDiscount)
    const totalDiscount = Math.min(
        (discounts
            .map((d) => d.discountAmount)
            .reduce((acc, cur) => acc + cur, 0) || 0),
        totalAmount,
    );

    // 쿠폰 할인만 분리 집계
    const totalCouponDiscount = discounts
        .filter((d) => !!d.couponId)
        .map((d) => d.discountAmount)
        .reduce((acc, cur) => acc + cur, 0) || 0;

    // 포인트 사용 금액
    const totalPointDiscount = discounts
        .filter((d) => d.type === 'POINT')
        .map((d) => d.discountAmount)
        .reduce((acc, cur) => acc + cur, 0) || 0;

    const paymentAmount = Math.max(totalAmount - totalDiscount, 0);

    const giftAmount = Math.min(
        request.giftPayments
            ?.reduce((acc, cur) => acc + cur.amount, 0) || 0,
        totalAmount,
    );

    const prepaidCardAmount = Math.min(
        request.prepaidCardPayments
            ?.reduce((acc, cur) => acc + cur.amount, 0) || 0,
        totalAmount,
    );

    const pgAmount = Math.max(0, (paymentAmount - giftAmount - prepaidCardAmount));

    const giftPayments = request.giftPayments?.map((it) => {
        const { amount, ...rest } = it;
        return {
            ...rest,
            amount: Math.min(amount, totalAmount),
        };
    });

    let { paymentMethod } = request;
    if (totalAmount > 0 && giftAmount === totalAmount) {
        paymentMethod = 'GIFT';
    }
    if (totalAmount > 0 && prepaidCardAmount === totalAmount) {
        paymentMethod = 'PREPAID_CARD';
    }

    return {
        ...request,
        orderLines: newOrderLines,
        totalAmount,
        paymentAmount,
        totalDiscount,
        totalCouponDiscount,
        totalPointDiscount,
        pgAmount,
        giftAmount,
        discounts,
        prepaidCardAmount,
        giftPayments,
        paymentMethod,
    };
};

export default orderWithTotalAmount;
